import moment from "moment";
import { JobStatus, IJob } from "../models"

const getDisplayableJobId = (jobId: string) => {
    const jobIdArr = jobId.split("-");
    return jobIdArr[jobIdArr.length - 1];
}

const getBadgeColor = (status: JobStatus) => {
    if (status === JobStatus.JOB_IMAGE_SCANNING
        || status === JobStatus.JOB_IMAGE_SCANNED
        || status === JobStatus.JOB_IMAGE_UPLOADING
        || status === JobStatus.JOB_IMAGE_UPLOADED) {
        return "gray";
    } else if (status === JobStatus.JOB_IN_PROGRESS || status === JobStatus.JOB_PROCESSING_LOCAL || status === JobStatus.JOB_SYNCING) {
        return "orange";
    } else if (status === JobStatus.JOB_COMPLETED) {
        return "green";
    } else if (status === JobStatus.JOB_FAILED) {
        return "red";
    } else {
        return "gray"
    }
}

const isDisplayableJob = (status: JobStatus) => {
    return [
        JobStatus.JOB_IMAGE_UPLOADED,
        JobStatus.JOB_PROCESSING_LOCAL,
        JobStatus.JOB_IN_PROGRESS,
        JobStatus.JOB_SYNCING,
        JobStatus.JOB_COMPLETED,
        JobStatus.JOB_FAILED
    ].includes(status)
}

const getStatusMessage = (status: JobStatus) => {
    if (status === JobStatus.JOB_IMAGE_SCANNING) {
        return "SCANNING";
    } else if (status === JobStatus.JOB_IMAGE_SCANNED) {
        return "SCANNED";
    } else if (status === JobStatus.JOB_IMAGE_UPLOADING) {
        return "UPLOADING";
    } else if (status === JobStatus.JOB_IMAGE_UPLOADED) {
        return "UPLOADED";
    } else if (status === JobStatus.JOB_IN_PROGRESS || status === JobStatus.JOB_PROCESSING_LOCAL) {
        return "ANALYZING";
    } else if (status === JobStatus.JOB_SYNCING) {
        return "SYNCING";
    } else if (status === JobStatus.JOB_COMPLETED) {
        return "COMPLETED";
    } else if (status === JobStatus.JOB_FAILED) {
        return "FAILED";
    } else {
        return "UNKNOWN";
    }
}

const getFormattedDate = (dateInEpoch: number) => {
    return moment(new Date(dateInEpoch * 1000)).format('D MMM, YY HH:mma')
}

export interface IAveragingValidationError {
    isValid: boolean;
    errorMessage?: string;
}

const validateJobsForAveraging = (
    selectedJobIds: string[],
    allJobs: IJob[]
): IAveragingValidationError => {
    // Minimum 2 jobs
    if (selectedJobIds.length < 2) {
        return {
            isValid: false,
            errorMessage: 'Please select at least 2 jobs to average'
        };
    }

    const selectedJobs = allJobs.filter(job =>
        selectedJobIds.includes(job.jobId)
    );

    if (selectedJobs.length !== selectedJobIds.length) {
        return {
            isValid: false,
            errorMessage: 'Some selected jobs are not available locally'
        };
    }

    // All jobs must be completed
    const allCompleted = selectedJobs.every(
        job => job.jobStatus === JobStatus.JOB_COMPLETED
    );
    if (!allCompleted) {
        return {
            isValid: false,
            errorMessage: 'All selected jobs must be completed'
        };
    }

    // All jobs must have same grainId
    const firstGrainId = selectedJobs[0].grainId;
    const sameGrainId = selectedJobs.every(
        job => job.grainId === firstGrainId
    );
    if (!sameGrainId) {
        return {
            isValid: false,
            errorMessage: 'All selected jobs must be for the same grain type'
        };
    }

    // All jobs must have same profileId
    const firstProfileId = selectedJobs[0].profileId;
    const sameProfileId = selectedJobs.every(
        job => job.profileId === firstProfileId
    );
    if (!sameProfileId) {
        return {
            isValid: false,
            errorMessage: 'All selected jobs must use the same profile'
        };
    }

    return { isValid: true };
}

const isAveragingJob = (job?: IJob | null): boolean => {
    return (job?.additionalAttributes?.sourceJobIds?.length ?? 0) > 0;
}

export {
    getBadgeColor,
    getDisplayableJobId,
    isDisplayableJob,
    getStatusMessage,
    getFormattedDate,
    validateJobsForAveraging,
    isAveragingJob
}
